import { getWeapon } from "@hupper/shared/weapons";
import type { GameClient } from "../net/GameClient";
import { hupperLogoSvg } from "./weaponIcons";

/**
 * HudBottomCenter — CS:GO-style centered vitals strip.
 *
 * Layout (anchored to screen center):
 *
 *     [ shield·100 ] 100  ·  ★  ·  20 | 120 ▏
 *        armor      hp      star    mag reserve+clips
 *                   ▔▔
 *
 * Two `flex:1` side slots hug the fixed-width center star, so the star stays
 * dead-center on screen and health / ammo sit symmetrically on either side.
 * Replaces the old split bottom-left vitals + bottom-right ammo readout.
 *
 * Visual-only. Every value comes straight from the server snapshot via
 * getLatestSelf(); we just render it.
 */
export class HudBottomCenter {
  private readonly client: GameClient;
  private readonly root: HTMLDivElement;
  // Vitals
  private readonly hpEl: HTMLSpanElement;
  private readonly hpBarFill: HTMLDivElement;
  private readonly armorWrap: HTMLDivElement;
  private readonly armorNum: HTMLSpanElement;
  private readonly helmetEl: SVGElement;
  private readonly starEl: HTMLDivElement;
  // Ammo
  private readonly ammoWrap: HTMLDivElement;
  private readonly magEl: HTMLSpanElement;
  private readonly reserveEl: HTMLSpanElement;
  private readonly clipsEl: HTMLSpanElement;

  private readonly unsubs: Array<() => void> = [];
  /** Last-rendered spare-magazine pip count; -1 forces the first build. */
  private clipCount = -1;

  constructor(client: GameClient) {
    this.client = client;
    this.root = document.createElement("div");
    this.root.id = "hud-center";
    this.root.innerHTML = `
      <div class="hc-side hc-left">
        <div class="hc-vitals">
          <div class="hc-armor">
            <svg class="hc-helmet" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path d="M12 3c-4.4 0-7.5 3.1-7.5 7.5V14H4v3h3v-2h10v2h3v-3h-.5v-3.5C19.5 6.1 16.4 3 12 3zm0 2.2c3.2 0 5.3 2.1 5.3 5.3V14H6.7v-3.5C6.7 7.3 8.8 5.2 12 5.2z"/>
            </svg>
            <div class="hc-shield-wrap">
              <svg class="hc-shield" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M12 1.5 3.5 5v6.2c0 5.4 3.7 10.4 8.5 11.8 4.8-1.4 8.5-6.4 8.5-11.8V5L12 1.5z"/>
              </svg>
              <span class="hc-armor-num">100</span>
            </div>
          </div>
          <div class="hc-health">
            <span class="hc-hp">100</span>
            <div class="hc-hp-bar"><div class="hc-hp-bar-fill"></div></div>
          </div>
        </div>
        <span class="hc-rule"></span>
      </div>
      <div class="hc-star hc-star-t">${hupperLogoSvg()}</div>
      <div class="hc-side hc-right">
        <span class="hc-rule"></span>
        <div class="hc-ammo-wrap">
          <span class="hc-ammo">
            <span class="hc-mag">—</span>
            <span class="hc-sep" aria-hidden="true"></span>
            <span class="hc-reserve">—</span>
            <span class="hc-clips" aria-hidden="true"></span>
          </span>
        </div>
      </div>
    `;
    this.hpEl = this.root.querySelector(".hc-hp")!;
    this.hpBarFill = this.root.querySelector(".hc-hp-bar-fill")!;
    this.armorWrap = this.root.querySelector(".hc-armor")!;
    this.armorNum = this.root.querySelector(".hc-armor-num")!;
    this.helmetEl = this.root.querySelector(".hc-helmet")!;
    this.starEl = this.root.querySelector(".hc-star")!;
    this.ammoWrap = this.root.querySelector(".hc-ammo-wrap")!;
    this.magEl = this.root.querySelector(".hc-mag")!;
    this.reserveEl = this.root.querySelector(".hc-reserve")!;
    this.clipsEl = this.root.querySelector(".hc-clips")!;

    document.body.appendChild(this.root);
    this.injectStyles();

    const refresh = () => this.render();
    refresh();
    this.unsubs.push(this.client.onPlayerChange((_, sid) => {
      if (sid === this.client.sessionId) refresh();
    }));
    this.unsubs.push(this.client.onMatchHud(() => refresh()));
    this.unsubs.push(this.client.onWeaponEquip((msg) => {
      if (msg.sessionId === this.client.sessionId) refresh();
    }));
  }

  destroy(): void {
    for (const u of this.unsubs) u();
    this.root.remove();
  }

  private render(): void {
    const self = this.client.getLatestSelf();

    // ── Health ──
    // Remaining life renders white (the fill); the depleted tail shows the red
    // track behind it — so a damaged bar reads white→red, left to right.
    const hp = Math.max(0, Math.min(100, Math.round(self?.health ?? 0)));
    this.hpEl.textContent = String(hp);
    this.hpBarFill.style.width = `${hp}%`;
    this.hpEl.classList.toggle("hc-dead", hp <= 0);

    // ── Armor / helmet ──
    const armor = Math.max(0, Math.round(self?.armor ?? 0));
    this.armorNum.textContent = String(armor);
    this.armorWrap.classList.toggle("hc-armor-empty", armor <= 0);
    this.helmetEl.classList.toggle("hc-helmet-on", !!self?.helmet);

    // ── Team star tint ──
    const team = self?.team === "ct" ? "ct" : "t";
    this.starEl.classList.toggle("hc-star-ct", team === "ct");
    this.starEl.classList.toggle("hc-star-t", team === "t");

    // ── Ammo (equipped firearm only) ──
    const id = self?.weaponId ?? "g18";
    let w;
    try {
      w = getWeapon(id);
    } catch {
      w = getWeapon("g18");
    }
    const hasAmmo = w.class !== "melee" && w.class !== "grenade" && w.magSize > 0;
    this.ammoWrap.classList.toggle("hidden", !hasAmmo);
    if (hasAmmo) {
      const mag = self?.magAmmo ?? w.magSize;
      const reserve = self?.reserveAmmo ?? w.reserveAmmo;
      this.magEl.textContent = String(mag);
      this.reserveEl.textContent = String(reserve);
      this.magEl.classList.toggle("low", mag <= Math.max(1, Math.floor(w.magSize * 0.2)));
      // One spare-magazine pip per reload still available in reserve.
      this.renderClips(Math.ceil(reserve / w.magSize));
    } else {
      this.renderClips(0);
    }
  }

  /** Rebuild the spare-magazine pips, skipping DOM churn when unchanged. */
  private renderClips(count: number): void {
    if (count === this.clipCount) return;
    this.clipCount = count;
    this.clipsEl.replaceChildren();
    for (let i = 0; i < count; i++) {
      const bar = document.createElement("i");
      bar.className = "hc-clip";
      this.clipsEl.appendChild(bar);
    }
    this.clipsEl.classList.toggle("empty", count === 0);
  }

  private injectStyles(): void {
    if (document.getElementById("hud-center-styles")) return;
    const style = document.createElement("style");
    style.id = "hud-center-styles";
    style.textContent = `
      #hud-center {
        position: fixed;
        left: 0;
        right: 0;
        bottom: 32px;
        z-index: 8;
        pointer-events: none;
        display: flex;
        align-items: flex-end;
        font-family: 'Geist', 'Trebuchet MS', sans-serif;
        color: #f5efe6;
      }
      /* Side slots expand equally so the star stays dead-center; a connector
         rule fills the gap between each readout and the star, pushing health
         and ammo apart (CS2-style). */
      #hud-center .hc-side {
        flex: 1 1 0;
        display: flex;
        align-items: flex-end;
        min-width: 0;
        gap: 18px;
      }
      #hud-center .hc-left  { justify-content: flex-end;   }
      #hud-center .hc-right { justify-content: flex-start; }

      /* Thin horizontal line linking each readout to the center star. */
      #hud-center .hc-rule {
        flex: 0 0 auto;
        width: clamp(110px, 15vw, 220px);
        height: 2px;
        margin-bottom: 22px;
        border-radius: 1px;
      }
      #hud-center .hc-left .hc-rule {
        background: linear-gradient(to right, rgba(245, 239, 230, 0), rgba(245, 239, 230, 0.42));
      }
      #hud-center .hc-right .hc-rule {
        background: linear-gradient(to left, rgba(245, 239, 230, 0), rgba(245, 239, 230, 0.42));
      }

      /* ── Center star ── */
      #hud-center .hc-star {
        flex: 0 0 auto;
        width: 40px;
        height: 40px;
        margin-bottom: 4px;
        display: flex;
        align-items: center;
        justify-content: center;
        color: #e8b14e;
        filter: drop-shadow(0 1px 3px rgba(0,0,0,0.8));
        transition: color 200ms ease;
      }
      #hud-center .hc-star.hc-star-ct { color: #6cb7ff; }
      #hud-center .hc-star.hc-star-t  { color: #e8b14e; }
      #hud-center .hc-star-svg { width: 30px; height: 30px; }
      #hud-center .hud-logo-svg { width: 32px; height: 32px; }

      /* ── Vitals (armor + health) ── */
      #hud-center .hc-vitals {
        display: flex;
        align-items: flex-end;
        gap: 14px;
      }
      #hud-center .hc-armor {
        position: relative;
        display: flex;
        flex-direction: column;
        align-items: center;
        margin-bottom: 8px;
      }
      #hud-center .hc-armor.hc-armor-empty { visibility: hidden; }
      #hud-center .hc-helmet {
        width: 18px; height: 18px; margin-bottom: -4px;
        color: #f5efe6;
        filter: drop-shadow(0 1px 1px rgba(0,0,0,0.85));
        opacity: 0; transform: translateY(2px);
        transition: opacity 120ms ease, transform 120ms ease;
      }
      #hud-center .hc-helmet.hc-helmet-on { opacity: 1; transform: translateY(0); }
      #hud-center .hc-shield-wrap {
        position: relative; width: 34px; height: 34px;
        display: flex; align-items: center; justify-content: center;
      }
      #hud-center .hc-shield {
        width: 100%; height: 100%; color: #f5efe6;
        filter: drop-shadow(0 1px 2px rgba(0,0,0,0.85));
      }
      #hud-center .hc-armor-num {
        position: absolute; top: 55%; left: 50%;
        transform: translate(-50%, -50%);
        font-size: 12px; font-weight: 800; line-height: 1;
        color: #1a1410; letter-spacing: -0.02em;
        font-variant-numeric: tabular-nums;
      }
      #hud-center .hc-health {
        display: flex; flex-direction: column; align-items: flex-end; gap: 5px;
      }
      #hud-center .hc-hp {
        font-size: 42px; font-weight: 800; line-height: 0.9;
        color: #f5efe6; letter-spacing: -0.04em;
        font-variant-numeric: tabular-nums;
        text-shadow: 0 2px 3px rgba(0,0,0,0.75);
        transition: color 160ms ease;
      }
      #hud-center .hc-hp.hc-dead { color: rgba(245,239,230,0.55); }
      /* Track = the damage color: whatever the white fill doesn't cover reads red. */
      #hud-center .hc-hp-bar {
        width: 88px; height: 4px;
        background: #d23b30;
        border-radius: 1px; overflow: hidden;
        box-shadow: 0 1px 2px rgba(0,0,0,0.5);
      }
      /* Remaining life: always white, never tinted — only its width changes. */
      #hud-center .hc-hp-bar-fill {
        width: 100%; height: 100%; background: #f5efe6;
        transition: width 240ms ease;
        will-change: width;
      }

      /* ── Ammo ── */
      #hud-center .hc-ammo-wrap {
        display: flex; flex-direction: column; align-items: flex-start; gap: 5px;
      }
      #hud-center .hc-ammo-wrap.hidden { visibility: hidden; }
      #hud-center .hc-ammo {
        display: inline-flex; align-items: flex-end; gap: 12px;
      }
      #hud-center .hc-mag {
        font-size: 42px; font-weight: 800; line-height: 0.9;
        color: #f5efe6; letter-spacing: -0.04em;
        font-variant-numeric: tabular-nums;
        text-shadow: 0 2px 3px rgba(0,0,0,0.75);
        transition: color 160ms ease;
      }
      #hud-center .hc-mag.low { color: #ff5252; }
      #hud-center .hc-sep {
        width: 2px; height: 28px; margin-bottom: 3px;
        border-radius: 1px; background: rgba(245,239,230,0.3);
      }
      #hud-center .hc-reserve {
        font-size: 24px; font-weight: 700; line-height: 1;
        color: rgba(245,239,230,0.7); letter-spacing: -0.02em;
        font-variant-numeric: tabular-nums;
        text-shadow: 0 1px 2px rgba(0,0,0,0.7); margin-bottom: 2px;
      }
      #hud-center .hc-clips {
        display: inline-flex; flex-direction: column;
        justify-content: flex-end; gap: 2px; margin-bottom: 5px;
      }
      #hud-center .hc-clips.empty { display: none; }
      #hud-center .hc-clip {
        width: 18px; height: 3px; border-radius: 1.5px;
        background: rgba(245,239,230,0.7);
        box-shadow: 0 1px 1px rgba(0,0,0,0.5);
      }
    `;
    document.head.appendChild(style);
  }
}
